import { MutationTree, Mutation } from 'vuex'
import {RootState, Notification, MenuItem, InputStyle, NavigationMode} from '@/store/types'
import _ from 'lodash'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import { DashButton } from '@/store/types'
import { Config } from '@/modules/core/config'
import { PrologueTheme } from '@/themes'


/**
 * Establece el numero de notificaciones sin leer
 *
 * @param type
 * @param namespace
 */
export const mutationName = (type: string, namespace = '') => {
    return (namespace ? namespace + '/' : '') + type
}

/**
 * Establece el numero de notificaciones sin leer
 */
function setUnreadNotifications (state: RootState, value: number) {
     // Si el centro de notificaciones esta desplegado no se añade como no leído
    if (state.notification.center.visible) {
        return
    }

    state.notification.center.unread = value
}

/**
 * Aumenta en uno el numero de notificaciones sin leer
 *
 * @param state
 */
function addUnreadNotification (state: RootState) {
    const current = state.notification.center.unread
    setUnreadNotifications(state, current + 1)
}

/**
 * Limpia la pila de notificaciones y cierra la ventana
 * @param state
 */
function clearNotificationStack (state: RootState) {
    state.notification.center.stack = []
    state.notification.center.visible = false
}

const mutations: MutationTree<RootState> = {
    /**
     * Muestra la pantalla de carga
     *
     * @param state
     */
    showLoadingScreen: (state, payload) => {
        if (_.has(payload, 'message')) {
            state.loadingScreen.message = payload.message
        }

        state.loadingScreen.fullscreen = payload.fullscreen
        state.loadingScreen.visible = true
        state.loadingScreen.brand = payload.brand || false
        state.loadingScreen.determinate = payload.determinate || false
     },

    /**
     * Oculta la pantalla de carga
     *
     * @param state
     */
    hideLoadingScreen: state => state.loadingScreen.visible = false,

    /**
     * Muestra una notificación con un mensaje y color
     *
     * @param state
     * @param payload
     */
    showNotification: (state, payload) => {
        const notification = new Notification(payload.message, payload.color)
        notification.goToAction = (payload.options || {}).goToAction
        notification.subject = (payload.options || {}).subject || ''

        state.notification.visible = true
        state.notification.message = notification.message
        state.notification.color   = notification.color

        // Se ha solicitado explícitamente que la notificación no sea añadida al centro
        // de notificaciones de la aplicación
        if ((payload.options || {}).notShowInCenter) {
            return
        }

        state.notification.center.stack.push(notification)

        // Aumenta el numero de notificaciones sin leer
        addUnreadNotification(state)
    },

    /**
     * Oculta la notificación actual
     */
    hideNotification: state => state.notification.visible = false,

    /**
     * Oculta la barra lateral de navegación
     *
     * @param state
     */
    hideSidenav: state => state.sidenav.visible = false,
    showSidenav: state => state.sidenav.visible = true,
    showToolbar: state => state.toolbar.visible = true,
    hideToolbar: state => state.toolbar.visible = false,
    setToolbarTitle: (state, title: string) => {
        state.toolbar.title = title
    },

    showNotificationCenter: (state, show: boolean) => {
         state.notification.center.visible = show
         _.delay(() => state.notification.center.unread = 0, 1000)
    },

    deleteNotification: (state, id: string) => {
        const index = state.notification.center.stack.findIndex(n => n.id === id)
        if (index === -1) {
            throw new Error(`No existe ninguna notificación con id ${id}`)
        }

        state.notification.center.stack.splice(index, 1)
    },
     setUnreadNotifications,
     addUnreadNotification,
     clearNotificationStack,
     setEnabledModules: (state, modules: string[]) => state.enabledModules = modules,
     toggleEnabledModulesFetched: (state, fetched: boolean) => state.enabledModulesFetched = fetched,
     setMenuItems: (state, items: MenuItem[]) => state.menuItems = items,
     setDashButtons: (state, dashButtons: DashButton[]) => state.dashButtons = dashButtons,
     toggleStopNavigation: (state, stopNavigation: boolean) => state.stopNavigation = stopNavigation,

     /**
      * Cambia el estado de progress de la pantalla de carga
      */
     setLoadingScreenProgress: (state, payload: {progress: number, global: boolean}) => {
        const progress = _.get(payload, 'progress', 0)
        const global = _.get(payload, 'global', false)
        if (global) {
            state.loadingScreen.progress = payload.progress || 0

            return
        }

        state.loadingScreen.progress = state.loadingScreen.progress + progress
     },

     setLoadingScreenProgressMessage: (state, message: string) => state.loadingScreen.progressMessage = message,
     setConfig: (state, config: Config) => state.config = config,
     setThemes: (state, themes: PrologueTheme[]) => state.themes = themes,
     setCurrentTheme: (state, theme: PrologueTheme) => state.currentTheme = theme,
     toggleThemeSelector: (state, showThemeSelector: boolean) => state.themeSelector = showThemeSelector,
     setInputStyle: (state, style: InputStyle) => state.inputStyle = style,
     toggleDarkMode: (state, darkMode: boolean) => state.darkMode = darkMode,
     // Habilita o des-habilita la navegación permanente
     togglePermanentNavigation:
     (state, permanentNavigation: boolean) => state.permanentNavigation = permanentNavigation,
     toggleDisplayTemporalNavigation:
     (state, displayTemporalNavigation: boolean) => state.displayTemporalNavigation = displayTemporalNavigation,
    setNavigationMode: (state, mode: NavigationMode) => state.navigationMode = mode,
    toggleSizeOverlay: (state, display: boolean) => state.displaySizeOverlay = display,
}

function testFlesh (p: number, f: number, s: number) {
    return 206.835 - ((62.3 * s) / p ) - (p / f)
}

export default mutations
